import { useEnergyStore } from '@/stores/useEnergyStore'
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react'

export const ConfirmModal = () => {
  const confirmState = useEnergyStore((s) => s.confirmState)
  const closeConfirm = useEnergyStore((s) => s.closeConfirm)
  const darkMode = useEnergyStore((s) => s.darkMode)

  if (!confirmState.isOpen) return null

  const handleConfirm = () => {
    if (confirmState.onConfirm) {
      confirmState.onConfirm()
    }
    closeConfirm()
  }

  // Determine styling based on type or isDestructive
  const type = confirmState.type || (confirmState.isDestructive ? 'warning' : 'info')

  const iconConfig = (() => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircle size={32} />,
          bgClass: darkMode ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600',
          btnClass: darkMode ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-900/20' : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200'
        }
      case 'error':
        return {
          icon: <AlertCircle size={32} />,
          bgClass: darkMode ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-50 text-rose-600',
          btnClass: darkMode ? 'bg-rose-600 hover:bg-rose-500 text-white shadow-rose-900/20' : 'bg-rose-500 hover:bg-rose-600 text-white shadow-rose-200'
        }
      case 'warning':
        return {
          icon: <AlertTriangle size={32} />,
          bgClass: darkMode ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600',
          btnClass: darkMode ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-amber-900/20' : 'bg-amber-500 hover:bg-amber-600 text-white shadow-amber-200'
        }
      case 'info':
      default:
        return {
          icon: <Info size={32} />,
          bgClass: darkMode ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600',
          btnClass: darkMode ? 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-900/20' : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-200'
        }
    }
  })()

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 backdrop-blur-md bg-slate-900/40">
      <div 
        className={`relative w-full max-w-sm rounded-2xl shadow-2xl border ${
          darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
      >
        <button
          onClick={closeConfirm}
          className={`absolute top-4 right-4 p-1.5 rounded-full transition-colors ${
            darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
          }`}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-6 pt-8">
          <div className="flex flex-col items-center text-center gap-4">
            <div className={`p-4 rounded-full ${iconConfig.bgClass}`}>
              {iconConfig.icon}
            </div>
            
            <div>
              <h2 id="confirm-title" className={`text-xl font-black mb-2 ${
                darkMode ? 'text-white' : 'text-slate-900'
              }`}>
                {confirmState.title}
              </h2>
              <p className={`text-sm ${
                darkMode ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {confirmState.message}
              </p>
            </div>
          </div>
        </div>

        <div className={`p-4 sm:px-6 sm:py-5 border-t flex items-center justify-end gap-3 rounded-b-2xl ${
          darkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-100'
        }`}>
          {!confirmState.hideCancel && (
            <button
              onClick={closeConfirm}
              className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                darkMode 
                  ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                  : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900'
              }`}
            >
              {confirmState.cancelText || 'Cancel'}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold shadow-sm transition-all active:scale-95 ${iconConfig.btnClass}`}
          >
            {confirmState.confirmText || 'OK'}
          </button>
        </div>
      </div>
    </div>
  )
}
